import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { ChannelModelRecord, UpdateChannelRequest } from '@nadoutong/contracts';
import { useChannelsList, useUpdateChannel } from '../api/useChannels.ts';
import { useChannelModelsList, useCreateChannelModel, useDeleteChannelModel } from '../api/useChannelModels.ts';
import { useModelsList } from '../api/useModels.ts';
import { Modal } from '../components/Modal.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

const INPUT_CLS =
  'w-full bg-surface-container-lowest border-none rounded-lg text-sm p-3 outline-none focus:ring-1 focus:ring-primary transition-colors';

export function ChannelDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const toast = useToast();
  const channelsQ = useChannelsList();
  const modelsQ = useChannelModelsList(id);
  const allModelsQ = useModelsList();
  const createMut = useCreateChannelModel();
  const deleteMut = useDeleteChannelModel();
  const updateChannelMut = useUpdateChannel();

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editingRate, setEditingRate] = useState(false);
  const [rateValue, setRateValue] = useState('');

  const channel = useMemo(
    () => (channelsQ.data ?? []).find((c) => c.id === id) ?? null,
    [channelsQ.data, id],
  );

  const models = useMemo(() => modelsQ.data ?? [], [modelsQ.data]);

  function handleDelete(cm: ChannelModelRecord): void {
    deleteMut.mutate(
      { channelId: id!, model: cm.model },
      { onSuccess: () => toast.success(t('channel_detail.toast.model_removed')) },
    );
  }

  function handleInfoSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (id === undefined) return;

    const fd = new FormData(e.currentTarget);
    const displayName = String(fd.get('display_name') ?? '').trim();
    const supplierName = String(fd.get('supplier_name') ?? '').trim();
    const rateMultiplier = parseFloat(String(fd.get('rate_multiplier') ?? ''));

    if (displayName.length === 0) {
      setEditError(t('channel_detail.edit_modal.display_name_required'));
      return;
    }
    if (!Number.isFinite(rateMultiplier) || rateMultiplier <= 0) {
      setEditError(t('channel_detail.edit_modal.rate_invalid'));
      return;
    }

    const body: UpdateChannelRequest = {
      display_name: displayName,
      supplier_name: supplierName.length > 0 ? supplierName : null,
      is_official: fd.get('is_official') === 'on',
      rate_multiplier: rateMultiplier,
    };

    setEditError(null);
    updateChannelMut.mutate(
      { id, body },
      {
        onSuccess: () => {
          toast.success(t('channel_detail.toast.info_updated'));
          setEditOpen(false);
        },
        onError: (err) => {
          const message = coerceErrorMessage(err);
          setEditError(message);
          toast.error(message);
        },
      },
    );
  }

  if (channelsQ.isLoading || modelsQ.isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-xl bg-surface-container" />
        ))}
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="rounded-xl bg-error-container px-6 py-4 text-on-error-container">
        {t('channel_detail.not_found')}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link to="/supply/channels" className="text-outline hover:text-on-surface transition-colors">
              <span aria-hidden="true" className="material-symbols-outlined text-[20px]">arrow_back</span>
            </Link>
            <h2 className="text-xl font-semibold text-on-surface">{channel.display_name}</h2>
            {channel.is_official ? (
              <span aria-hidden="true" className="material-symbols-outlined text-primary text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                verified
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-outline ml-8">
            <span className="inline-flex items-center px-2 py-0.5 rounded bg-tertiary/10 text-tertiary text-[10px] font-bold uppercase mr-2">
              {channel.vendor}
            </span>
            <span className="font-mono">{channel.slug}</span>
            {channel.supplier_name ? <> · {channel.supplier_name}</> : null}
            {' · '}{models.length} {t('channel_detail.models_count')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => { setEditError(null); setEditOpen(true); }}
            data-testid="channel-edit-info-btn"
            className="flex items-center gap-2 rounded-lg bg-surface-container px-4 py-2 text-sm font-semibold text-on-surface transition-all hover:bg-surface-bright active:scale-95"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">edit</span>
            {t('channel_detail.edit_info')}
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="channel-add-model-btn"
            className="flex items-center gap-2 rounded-lg bg-primary-container px-4 py-2 text-sm font-semibold text-on-primary transition-all hover:brightness-110 active:scale-95 shadow-lg shadow-primary/10"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">add</span>
            {t('channel_detail.add_model')}
          </button>
        </div>
      </div>

      {/* Rate multiplier */}
      <div className="flex items-center gap-4 rounded-xl bg-surface-container p-4">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">{t('channel_detail.rate_multiplier')}</span>
        {editingRate ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-outline">×</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={rateValue}
              onChange={(e) => setRateValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const val = parseFloat(rateValue);
                  if (Number.isFinite(val) && val > 0) {
                    updateChannelMut.mutate({ id: id!, body: { rate_multiplier: val } }, {
                      onSuccess: () => { toast.success(t('channel_detail.toast.rate_updated')); setEditingRate(false); },
                      onError: (err) => toast.error(coerceErrorMessage(err)),
                    });
                  }
                }
                if (e.key === 'Escape') setEditingRate(false);
              }}
              className="w-24 bg-surface-container-lowest border-none rounded text-sm p-1.5 outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            <button
              type="button"
              onClick={() => {
                const val = parseFloat(rateValue);
                if (Number.isFinite(val) && val > 0) {
                  updateChannelMut.mutate({ id: id!, body: { rate_multiplier: val } }, {
                    onSuccess: () => { toast.success(t('channel_detail.toast.rate_updated')); setEditingRate(false); },
                    onError: (err) => toast.error(coerceErrorMessage(err)),
                  });
                }
              }}
              className="text-primary hover:text-primary/80"
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[16px]">check</span>
            </button>
            <button type="button" onClick={() => setEditingRate(false)} className="text-outline hover:text-on-surface">
              <span aria-hidden="true" className="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setRateValue(String(channel.rate_multiplier)); setEditingRate(true); }}
            className="text-lg font-bold text-primary hover:underline"
          >
            ×{channel.rate_multiplier.toFixed(2)}
          </button>
        )}
        <span className="text-[11px] text-outline">{t('channel_detail.rate_hint')}</span>
      </div>

      {models.length === 0 ? (
        <div className="rounded-xl bg-surface-container px-6 py-16 text-center text-outline" data-testid="channel-models-empty">
          {t('channel_detail.empty')}
        </div>
      ) : (
        <div className="rounded-xl bg-surface-container overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-container-high/20">
                <th scope="col" className="px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('channel_detail.col.model')}</th>
                <th scope="col" className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {models.map((cm) => (
                <tr key={cm.model} className="hover:bg-surface-bright/30 transition-colors group">
                  <td className="px-6 py-3 text-sm font-medium">{cm.model}</td>
                  <td className="px-6 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(cm)}
                      disabled={deleteMut.isPending}
                      className="text-outline opacity-0 group-hover:opacity-100 hover:text-error transition-all disabled:opacity-40"
                    >
                      <span aria-hidden="true" className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BatchAddModal
        channelId={id!}
        channelVendor={channel.vendor}
        existingModels={modelsQ.data ?? []}
        allModels={allModelsQ.data ?? []}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        createMut={createMut}
      />

      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} titleId="channel-edit-title">
        <div className="p-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h3 id="channel-edit-title" className="text-xl font-semibold text-on-surface">{t('channel_detail.edit_modal.title')}</h3>
              <p className="mt-1 text-xs text-outline">
                <span className="inline-flex items-center rounded bg-tertiary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-tertiary">
                  {channel.vendor}
                </span>
                <span className="ml-2 font-mono">{channel.slug}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              className="text-outline transition-colors hover:text-on-surface"
            >
              <span aria-hidden="true" className="material-symbols-outlined">close</span>
            </button>
          </div>

          <form onSubmit={handleInfoSubmit} noValidate className="space-y-6">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">{t('channels.create_modal.display_name')}</label>
              <input
                name="display_name"
                defaultValue={channel.display_name}
                className={INPUT_CLS}
                data-testid="channel-edit-display-name"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">{t('channels.create_modal.supplier_name')}</label>
              <input
                name="supplier_name"
                defaultValue={channel.supplier_name ?? ''}
                className={INPUT_CLS}
                data-testid="channel-edit-supplier-name"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">
                {t('channels.create_modal.rate_multiplier')}
              </label>
              <input
                name="rate_multiplier"
                type="number"
                step="0.01"
                min="0.01"
                defaultValue={channel.rate_multiplier}
                className={INPUT_CLS}
                data-testid="channel-edit-rate-multiplier"
              />
              <p className="mt-1 text-[11px] text-outline">{t('channels.create_modal.rate_multiplier_hint')}</p>
            </div>

            <div className="flex items-center justify-between rounded-lg bg-surface-container-low p-4">
              <div>
                <p className="text-sm font-medium">{t('channels.create_modal.is_official')}</p>
                <p className="text-xs text-outline">{t('channels.create_modal.is_official_hint')}</p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  name="is_official"
                  type="checkbox"
                  defaultChecked={channel.is_official}
                  className="peer sr-only"
                  data-testid="channel-edit-is-official"
                />
                <div className="h-6 w-11 rounded-full bg-outline-variant after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-on-surface after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full" />
              </label>
            </div>

            {editError !== null ? (
              <div className="rounded-lg bg-error-container px-3 py-2 text-xs text-on-error-container" data-testid="channel-edit-error">
                {editError}
              </div>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                className="rounded-lg px-6 py-2.5 text-sm font-semibold text-on-surface-variant transition-all hover:bg-surface-variant"
              >
                {t('channels.create_modal.cancel')}
              </button>
              <button
                type="submit"
                disabled={updateChannelMut.isPending}
                data-testid="channel-edit-submit"
                className="rounded-lg bg-primary-container px-8 py-2.5 text-sm font-semibold text-on-primary shadow-lg shadow-primary/10 transition-all hover:brightness-110 active:scale-95 disabled:pointer-events-none disabled:opacity-40"
              >
                {updateChannelMut.isPending ? t('channel_detail.edit_modal.saving') : t('channel_detail.edit_modal.save')}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}

function BatchAddModal({
  channelId,
  channelVendor,
  existingModels,
  allModels,
  isOpen,
  onClose,
  createMut,
}: {
  readonly channelId: string;
  readonly channelVendor: string;
  readonly existingModels: readonly ChannelModelRecord[];
  readonly allModels: readonly {
    vendor: string;
    model: string;
    display_name: string | null;
    shelf_status?: string;
    deleted_at?: string | null;
  }[];
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly createMut: ReturnType<typeof useCreateChannelModel>;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const existingSet = useMemo(() => {
    const s = new Set<string>();
    for (const cm of existingModels) {
      s.add(cm.model);
    }
    return s;
  }, [existingModels]);

  const availableModels = useMemo(() => {
    return allModels
      .filter(
        (m) =>
          m.vendor === channelVendor &&
          m.deleted_at === null &&
          m.shelf_status === 'listed' &&
          !existingSet.has(m.model),
      )
      .sort((a, b) => a.model.localeCompare(b.model));
  }, [allModels, channelVendor, existingSet]);

  function toggleModel(model: string): void {
    const next = new Set(selected);
    if (next.has(model)) {
      next.delete(model);
    } else {
      next.add(model);
    }
    setSelected(next);
  }

  function toggleAll(): void {
    if (selected.size === availableModels.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(availableModels.map((m) => m.model)));
    }
  }

  async function handleBatchAdd(): Promise<void> {
    if (selected.size === 0) return;
    setAdding(true);
    let successCount = 0;
    for (const model of selected) {
      try {
        await createMut.mutateAsync({
          channel_id: channelId,
          model,
        });
        successCount++;
      } catch {
        // continue with remaining
      }
    }
    setAdding(false);
    if (successCount > 0) {
      toast.success(`${successCount} ${t('channel_detail.toast.models_added')}`);
    }
    setSelected(new Set());
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} titleId="channel-batch-add-title">
      <div className="p-8 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h3 id="channel-batch-add-title" className="text-lg font-semibold text-on-surface">{t('channel_detail.add_modal.title')}</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded bg-tertiary/10 text-tertiary text-[10px] font-bold uppercase">
              {channelVendor}
            </span>
          </div>
          <button type="button" onClick={onClose} className="text-outline hover:text-on-surface transition-colors">
            <span aria-hidden="true" className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 rounded-lg bg-surface-container-low mb-4">
          {availableModels.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-outline">{t('channel_detail.add_modal.no_models')}</p>
          ) : (
            <>
              <div className="sticky top-0 bg-surface-container-high/80 backdrop-blur px-4 py-2 flex items-center justify-between border-b border-outline-variant/10">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.size === availableModels.length && availableModels.length > 0}
                    onChange={toggleAll}
                    className="accent-primary w-4 h-4"
                  />
                  <span className="text-xs font-semibold text-outline">
                    {t('channel_detail.add_modal.select_all')} ({availableModels.length})
                  </span>
                </label>
                {selected.size > 0 && (
                  <span className="text-xs text-primary font-semibold">{selected.size} {t('channel_detail.add_modal.selected')}</span>
                )}
              </div>
              <div className="divide-y divide-outline-variant/10">
                {availableModels.map((m) => (
                  <label key={m.model} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-surface-bright/30 transition-colors">
                    <input
                      type="checkbox"
                      checked={selected.has(m.model)}
                      onChange={() => toggleModel(m.model)}
                      className="accent-primary w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-on-surface">{m.display_name ?? m.model}</span>
                      <span className="ml-2 text-xs font-mono text-outline">{m.model}</span>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-sm font-semibold text-on-surface-variant hover:bg-surface-variant transition-all">
            {t('channel_detail.add_modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleBatchAdd}
            disabled={adding || selected.size === 0}
            className="px-6 py-2 rounded-lg text-sm font-semibold bg-primary-container text-on-primary hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-primary/10 disabled:opacity-40 disabled:pointer-events-none"
          >
            {adding
              ? t('channel_detail.add_modal.adding')
              : `${t('channel_detail.add_modal.submit')} ${selected.size > 0 ? `(${selected.size})` : ''}`
            }
          </button>
        </div>
      </div>
    </Modal>
  );
}
